import { API_BASE_URL } from '../config/constants';

/**
 * Utilitaires pour les appels API
 */

/**
 * Effectue une requête fetch avec gestion d'erreurs
 */
export async function apiRequest(endpoint, options = {}) {
  // Normaliser l'URL pour éviter les doubles /api
  let baseUrl = API_BASE_URL.trim();
  let normalizedEndpoint = endpoint.trim();

  // Toujours supprimer /api/ au début de l'endpoint car API_BASE_URL contient déjà /api
  if (normalizedEndpoint.startsWith('/api/')) {
    normalizedEndpoint = normalizedEndpoint.substring(4); // Enlève "/api"
  } else if (normalizedEndpoint.startsWith('/api')) {
    normalizedEndpoint = normalizedEndpoint.substring(4); // Enlève "/api"
  }

  // S'assurer que l'endpoint commence par /
  if (!normalizedEndpoint.startsWith('/')) {
    normalizedEndpoint = '/' + normalizedEndpoint;
  }

  // Si baseUrl se termine par /, on l'enlève pour éviter les doubles slashes
  if (baseUrl.endsWith('/')) {
    baseUrl = baseUrl.slice(0, -1);
  }

  const url = `${baseUrl}${normalizedEndpoint}`;

  // Ajouter le token JWT pour les routes admin
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Si c'est une route admin, ajouter le token JWT
  if (normalizedEndpoint.startsWith('/admin/')) {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      headers.Authorization = `Bearer ${adminToken}`;
    }
  }

  // Le token JWT est maintenant dans un cookie httpOnly
  // Il sera envoyé automatiquement avec credentials: 'include'
  // Plus besoin de l'ajouter manuellement dans les headers

  const defaultOptions = {
    credentials: 'include',
    headers,
    // Éviter les 304 : le corps vide provoque "Unexpected token" au parse JSON
    cache: 'no-store',
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    if (!response.ok) {
      let errorData = {};
      if (isJson) {
        try {
          errorData = await response.json();
        } catch {
          errorData = {};
        }
      }
      const message =
        errorData.details ||
        errorData.error ||
        `Erreur HTTP: ${response.status}`;
      throw new Error(message);
    }

    if (!isJson) {
      const text = await response.text();
      if (
        text.trim().toLowerCase().startsWith('<!doctype') ||
        text.trim().startsWith('<html')
      ) {
        throw new Error(
          "Le serveur a renvoyé une page HTML au lieu de données JSON. Vérifiez que l'API backend est accessible et que REACT_APP_API_URL pointe vers le bon serveur."
        );
      }
      throw new Error('Réponse serveur invalide (attendu JSON)');
    }

    // Corps vide (ex. 200 sans body) : éviter JSON.parse("") qui lance "Unexpected token"
    const text = await response.text();
    if (!text || text.trim() === '') {
      return {};
    }

    try {
      return JSON.parse(text);
    } catch (parseError) {
      if (
        parseError instanceof SyntaxError &&
        parseError.message?.includes('JSON')
      ) {
        throw new Error(
          "Le serveur a renvoyé une réponse invalide (HTML au lieu de JSON). Vérifiez que l'API backend est accessible et que REACT_APP_API_URL pointe vers le bon serveur."
        );
      }
      throw parseError;
    }
  } catch (error) {
    console.error(`Erreur API ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Effectue une requête GET
 */
export async function apiGet(endpoint, options = {}) {
  return apiRequest(endpoint, { ...options, method: 'GET' });
}

/**
 * Effectue une requête POST
 */
export async function apiPost(endpoint, data = {}, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Effectue une requête PUT
 */
export async function apiPut(endpoint, data = {}, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Effectue une requête PATCH
 */
export async function apiPatch(endpoint, data = {}, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Effectue une requête DELETE
 */
export async function apiDelete(endpoint, options = {}) {
  return apiRequest(endpoint, { ...options, method: 'DELETE' });
}
